import React from 'react';
import { Helmet } from 'react-helmet-async';
import { RoutingStore } from '../../stores/RoutingStore';
import { MetaStore } from '../../stores/MetaStore';
import { ScreenStore } from '../../stores/ScreenStore';
import { ConfigStore } from '../../stores/ConfigStore';
import { CategoriesStore } from '../../stores/CategoriesStore';
import { injectStoresV1 } from '../../stores/injectStoresV1';
import { WithTranslations, withTranslations } from '../../../utils/withTranslations';
import { generateBreadcrumbListScriptPropsV1 } from './generateBreadcrumbListScriptPropsV1';

export interface HtmlHeadProps extends WithTranslations {
  routingStore: RoutingStore;
  metaStore: MetaStore;
  screenStore: ScreenStore;
  configStore: ConfigStore;
  categoriesStore: CategoriesStore;
}

export const HtmlHeadCmp: React.FC<HtmlHeadProps> = ({
  screenStore,
  routingStore,
  configStore: {
    config: { currentLanguage, domain, supportedLanguages },
  },
  metaStore,
  categoriesStore,
  t,
}) => {
  const { routes, alternateCanonicalLink, canonicalLink, location } = routingStore;
  const mobileViewportWidth = screenStore.isLandscape ? 640 : 320;
  const isPreviewPage = screenStore.screen === 'preview';
  const canonicalUrl = routes.toAbsolute(canonicalLink ?? location.pathname);
  const generateAlternateUrl = (lang: string = 'en'): string =>
    `https://${lang === 'en' ? 'www' : lang}.${domain}${alternateCanonicalLink}`;
  const breadcrumbListScriptProps = generateBreadcrumbListScriptPropsV1({
    routingStore,
    categoriesStore,
    t,
  });

  return (
    <Helmet script={[breadcrumbListScriptProps]}>
      <title>{metaStore.documentTitle}</title>
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@wix" />
      <meta name="twitter:title" content={metaStore.documentTitle} />
      <meta name="twitter:description" content={metaStore.description} />
      <meta
        name="twitter:image:src"
        content={`//static.parastorage.com/services/wix-html-template-viewer/1.1015.0/images/seo/templates-${metaStore.language}.jpg`}
      />

      <meta name="description" content={metaStore.description} />
      <meta
        name="viewport"
        content={`width=${mobileViewportWidth}, user-scalable=no, viewport-fit=cover, shrink-to-fit=no${
          isPreviewPage ? '' : ', initial-scale=1'
        }`}
      />
      <meta name="og:type" content="website" />
      <meta name="og:title" content={metaStore.documentTitle} />
      <meta name="og:description" content={metaStore.description} />
      <meta name="og:url" content={canonicalUrl} />

      {metaStore.noindex && <meta name="robots" content="noindex, nofollow" />}
      {!metaStore.noFollow && [
        <link
          rel="alternate"
          key="x-default"
          hrefLang="x-default"
          href={currentLanguage === 'en' ? canonicalUrl : generateAlternateUrl('en')}
        />,
        <link rel="alternate" key={currentLanguage} hrefLang={currentLanguage} href={canonicalUrl} />,
        ...supportedLanguages.map(
          (lang) =>
            currentLanguage !== lang && (
              <link rel="alternate" key={lang} hrefLang={lang} href={generateAlternateUrl(lang)} />
            ),
        ),
      ]}
      {!metaStore.noFollow && <link rel="canonical" href={canonicalUrl} />}
    </Helmet>
  );
};

export const HtmlHead = withTranslations(
  injectStoresV1('routingStore', 'metaStore', 'screenStore', 'configStore', 'categoriesStore')(HtmlHeadCmp),
);
